import axios from 'axios';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import _ from 'underscore';

import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../../../utils/async';
import { emailRegex } from '../../../../utils/text/validation';
import { isDefined } from '../../../../utils/variables';
import { Button } from '../../button/button';
import { Icon } from '../../icon/icon';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { Administrator, FormStateContext } from '../state/form-state-provider';
import { StepperContext } from '../state/stepper-provider';
import { isEmptyField } from '../utils';
import { Form, Grid } from './styles';

enum EmailExistanceStatus {
  Error,
  Exists,
  InProgress,
  NotChecked,
  NotExists,
}

const AdministratorAccountStep = () => {
  const { t } = useTranslation();
  const { admin, setAdmin } = React.useContext(FormStateContext);
  const { prevStep, nextStep } = React.useContext(StepperContext);
  const abortController = React.useRef<AbortController>();

  const [emailExistanceStatus, setEmailExistanceStatus] = React.useState(
    EmailExistanceStatus.NotChecked,
  );

  const {
    register,
    handleSubmit,
    getValues,
    resetField,
    getFieldState,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<Administrator>({
    defaultValues: admin,
  });

  const remainingFieldsRequired = React.useRef(false);
  const onlyEmailEnabled =
    isDefined(errors.email) ||
    emailExistanceStatus === EmailExistanceStatus.Error ||
    emailExistanceStatus === EmailExistanceStatus.InProgress ||
    emailExistanceStatus === EmailExistanceStatus.NotChecked ||
    emailExistanceStatus === EmailExistanceStatus.Exists;

  /**
   * Checks if current e-mail address already exists in database.
   * Returns false on any error and true when validation should be considered successful (on
   * email exists and email not exists).
   */
  const debounceSearchTextInput = React.useMemo(
    () =>
      _.throttle(async () => {
        if (!getValues('email')) {
          return;
        }

        try {
          abortController.current?.abort();
          abortController.current = new AbortController();

          setEmailExistanceStatus(EmailExistanceStatus.InProgress);
          clearErrors();

          const userService = SkyMapAxiosServiceFactory.instance.createUserServiceV0();
          await userService.isUserEmailRegistered(
            {
              query: { email: getValues('email') },
            },
            abortController.current,
          );

          // Reset fields when email exists.
          resetField('firstName');
          resetField('lastName');
          resetField('phone');

          remainingFieldsRequired.current = false;
          setEmailExistanceStatus(EmailExistanceStatus.Exists);
          return EmailExistanceStatus.Exists;
        } catch (err: unknown) {
          remainingFieldsRequired.current = true;

          if (axios.isAxiosError(err)) {
            // Do nothing if request is cancelled.
            if (axios.isCancel(err)) {
              return;
            }

            if ((err as any)?.response?.status === 404) {
              setEmailExistanceStatus(EmailExistanceStatus.NotExists);
              return EmailExistanceStatus.NotExists;
            }
          }

          setEmailExistanceStatus(EmailExistanceStatus.Error);
          return EmailExistanceStatus.Error;
        }
      }, 200),
    [clearErrors, getValues, resetField],
  );

  React.useEffect(() => {
    iiafe(async () => {
      // Validate on component initialization only when email is set.
      if (getValues('email') !== '') {
        await trigger('email');
      }
    });
  }, [getValues, trigger]);

  React.useEffect(() => {
    return () => {
      // Abort any current request when component dismounts.
      abortController?.current?.abort();
    };
  }, []);

  const saveFormValues = (data: Administrator) => {
    setAdmin(data);
  };

  return (
    <Form
      onSubmit={handleSubmit((data) => {
        saveFormValues(data);
        nextStep();
      })}
      onSubmitCapture={(e) => {
        getFieldState('email').error = { type: 'validate' };
      }}
    >
      <Grid>
        <LabelledContainer
          required={true}
          text={
            <Stack alignItems="baseline" direction="row" inline={true} spacing={0.5}>
              {emailExistanceStatus === EmailExistanceStatus.InProgress && (
                <Icon icon={['fad', 'spinner']} size="sm" spin={true} />
              )}
              {t('registerCompany.step2.email', { ns: 'components' })}
            </Stack>
          }
        >
          {(formElementId) => (
            <TextBox
              {...register('email', {
                required: true,
                pattern: {
                  value: emailRegex,
                  message: t('registerCompany.step2.emailIsInvalid', { ns: 'components' }),
                },
                validate: async () => {
                  const status = await debounceSearchTextInput();
                  return (
                    status === EmailExistanceStatus.Exists ||
                    status === EmailExistanceStatus.NotExists
                  );
                },
                onChange: async () => {
                  await trigger('email');
                },
              })}
              autoFocus={true}
              errorMessage={errors.email?.message}
              id={formElementId()}
              maxLength={128}
            />
          )}
        </LabelledContainer>

        <LabelledContainer
          required={!onlyEmailEnabled}
          text={t('registerCompany.step2.firstName', { ns: 'components' })}
        >
          {(formElementId) => (
            <TextBox
              {...register('firstName', {
                required: remainingFieldsRequired.current,
                validate: (value) =>
                  remainingFieldsRequired.current ? !isEmptyField(value) : true,
              })}
              // disabled prop inside register -> no validation.
              disabled={onlyEmailEnabled}
              errorMessage={errors.firstName?.message}
              id={formElementId()}
              maxLength={128}
            />
          )}
        </LabelledContainer>

        <LabelledContainer
          required={!onlyEmailEnabled}
          text={t('registerCompany.step2.lastName', { ns: 'components' })}
        >
          {(formElementId) => (
            <TextBox
              {...register('lastName', {
                required: remainingFieldsRequired.current,
                validate: (value) =>
                  remainingFieldsRequired.current ? !isEmptyField(value) : true,
              })}
              // disabled prop inside register -> no validation.
              disabled={onlyEmailEnabled}
              errorMessage={errors.lastName?.message}
              id={formElementId()}
              maxLength={128}
            />
          )}
        </LabelledContainer>

        <LabelledContainer
          required={!onlyEmailEnabled}
          text={t('registerCompany.step2.phone', { ns: 'components' })}
        >
          {(formElementId) => (
            <TextBox
              {...register('phone', {
                required: remainingFieldsRequired.current,
                validate: (value) =>
                  remainingFieldsRequired.current ? !isEmptyField(value) : true,
              })}
              // disabled prop inside register -> no validation.
              disabled={onlyEmailEnabled}
              errorMessage={errors.phone?.message}
              id={formElementId()}
              maxLength={128}
            />
          )}
        </LabelledContainer>
      </Grid>
      {emailExistanceStatus === EmailExistanceStatus.Exists && (
        <InfoBox color="yellow">
          <Trans
            components={{ br: <br /> }}
            i18nKey="registerCompany.step2.emailAlreadyExistsMessage"
            ns="components"
          />
        </InfoBox>
      )}
      {emailExistanceStatus === EmailExistanceStatus.Error && (
        <InfoBox color="red">
          <Stack justifyContent="left" spacing={0.5}>
            {t('registerCompany.step2.emailCouldNotBeVerified', { ns: 'components' })}
            <Button alignSelf="flex-start" variant={'contained'} onClick={() => trigger('email')}>
              {t('retry', { ns: 'common' })}
            </Button>
          </Stack>
        </InfoBox>
      )}
      <Stack
        alignItems="center"
        direction="row"
        justifyContent={'space-between'}
        margin="1em 0 0 0"
        spacing={0.5}
      >
        <Button
          color="secondary"
          variant="contained"
          onClick={() => {
            saveFormValues(getValues());
            prevStep();
          }}
        >
          {t('previous', { ns: 'common' })}
        </Button>

        <Button
          color="primary"
          disabled={emailExistanceStatus === EmailExistanceStatus.InProgress}
          type="submit"
          variant="contained"
        >
          {t('next', { ns: 'common' })}
        </Button>
      </Stack>
    </Form>
  );
};

export { AdministratorAccountStep };
